import React, { useEffect } from 'react';
import { XMarkIcon, MagnifyingGlassMinusIcon, MagnifyingGlassPlusIcon } from '@heroicons/react/24/outline';

/**
 * ImageLightbox - תצוגת תמונה מוגדלת
 * 
 * עוצר scroll של הגוף כשפתוח
 * תומך במסכי מגע וב-zoom
 * נסגר בלחיצה מחוץ לתמונה או על כפתור X או ESC
 */
const ImageLightbox = ({ src, alt, isOpen, onClose }) => {
  const [zoom, setZoom] = React.useState(1);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setZoom(1); // Reset zoom when opening
    } else {
      document.body.style.overflow = '';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close on ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleEsc);
    }

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    // Only close if clicking on the backdrop, not the image
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.5, 3)); // Max 3x zoom
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.5, 1)); // Min 1x zoom
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4 animate-fadeIn"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="תצוגת תמונה מוגדלת"
    >
      {/* Close Button - Top Right */}
      <button
        onClick={onClose}
        className="absolute top-4 left-4 sm:left-8 p-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full transition-all z-10 backdrop-blur-sm"
        aria-label="סגור"
      >
        <XMarkIcon className="h-6 w-6" />
      </button>

      {/* Zoom Controls */}
      <div className="absolute top-4 right-4 sm:right-8 flex gap-2 z-10">
        <button
          onClick={handleZoomOut}
          disabled={zoom <= 1}
          className="p-2 bg-white bg-opacity-20 hover:bg-opacity-30 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-full transition-all backdrop-blur-sm"
          aria-label="הקטן"
        >
          <MagnifyingGlassMinusIcon className="h-6 w-6" />
        </button>
        <button
          onClick={handleZoomIn}
          disabled={zoom >= 3}
          className="p-2 bg-white bg-opacity-20 hover:bg-opacity-30 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-full transition-all backdrop-blur-sm"
          aria-label="הגדל"
        >
          <MagnifyingGlassPlusIcon className="h-6 w-6" />
        </button>
      </div>

      {/* Zoom Level Indicator */}
      {zoom > 1 && (
        <div className="absolute bottom-4 right-1/2 translate-x-1/2 px-3 py-1 bg-white bg-opacity-20 text-white text-sm rounded-full backdrop-blur-sm z-10">
          {(zoom * 100).toFixed(0)}%
        </div>
      )}

      {/* Image Container */}
      <div className="relative max-w-full max-h-full overflow-auto">
        <img
          src={src}
          alt={alt || 'תמונת מוצר'}
          className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl transition-transform duration-300 ease-out"
          style={{
            transform: `scale(${zoom})`,
            transformOrigin: 'center',
            cursor: zoom > 1 ? 'grab' : 'default'
          }}
          onClick={(e) => e.stopPropagation()} // Prevent closing when clicking image
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/600x400?text=תמונה+לא+זמינה';
          }}
        />
      </div>

      {/* Image Name/Alt */}
      {alt && (
        <div className="absolute bottom-4 left-4 right-4 sm:left-8 sm:right-8 text-center">
          <p className="text-white text-sm sm:text-base bg-black bg-opacity-50 px-4 py-2 rounded-lg backdrop-blur-sm inline-block max-w-full truncate">
            {alt}
          </p>
        </div>
      )}

      {/* Instructions */}
      <div className="absolute bottom-20 left-1/2 -translate-x-1/2 text-white text-xs opacity-70 text-center px-4">
        <p className="hidden sm:block">לחץ ESC או מחוץ לתמונה לסגירה</p>
        <p className="sm:hidden">לחץ מחוץ לתמונה לסגירה</p>
      </div>
    </div>
  );
};

export default ImageLightbox;


